import React, { useState, useEffect } from "react";
import { Autocomplete } from "@mui/material";
import { propertyAPI } from "../../../services/api/property";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
  Collapse,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import {
  Edit,
  Delete,
  Add,
  UploadFile,
  Refresh,
  Info,
  Person,
  ContactMail,
  Description,
  AccountBalance,
  DateRange,
} from "@mui/icons-material";
import { tenantAPI } from "../../../services/api/tenant";
import { mergeAndSave } from "../../../utils/storageUtils";

const positionLabels = {
  left: "Links",
  right: "Rechts",
  center: "Mitte",
  center_right: "Mitte Rechts",
  center_left: "Mitte Links",
  front: "Vorne",
  rear: "Hinten",
  entire_floor: "Ganzer Stock",
  ground_floor: "Erdgeschoss",
  upper_floor: "Obergeschoss",
  penthouse: "Penthouse",
  basement: "Keller",
  duplex: "Duplex",
};

const TenantTab = ({ initialSearch = "" }) => {
  const [tenants, setTenants] = useState(() => {
    const saved = localStorage.getItem("tenants");
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState(() => {
    const saved = localStorage.getItem("properties");
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const fetchProperties = async () => {
    try {
      const response = await propertyAPI.getProperties({ limit: 1000 });
      setProperties(response.data);
      mergeAndSave("properties", response.data);
    } catch (err) {
      console.error("Fehler beim Laden der Immobilien", err);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState(initialSearch);

  useEffect(() => {
    if (initialSearch) {
      const trimmed = String(initialSearch).trim();
      const asNumber = Number(trimmed);
      if (trimmed !== "" && !Number.isNaN(asNumber)) {
        setSearch(`id=${trimmed}`);
      } else {
        setSearch(initialSearch);
      }
    }
  }, [initialSearch]);

  const [openDialog, setOpenDialog] = useState(false);
  const [openImportInfoDialog, setOpenImportInfoDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [expandedTenantId, setExpandedTenantId] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  const initialFormState = {
    tenant_number: "",
    name: "",
    email: "",
    phone: "",
    property_ids: [],
    rent_net: "",
    utility_advance: "",
    contract_size: "",
    deposit_amount: "",
    bank_account: "",
    notice_period: "",
    move_in_date: "",
    move_out_date: "",
    last_rent_increase: "",
    is_active: true,
    description: "",
    contact_persons: [],
  };
  const [formData, setFormData] = useState(initialFormState);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const skip = page * rowsPerPage;
      const response = await tenantAPI.getTenants({
        skip,
        limit: rowsPerPage,
        search,
      });
      setTenants(response.data);
      mergeAndSave("tenants", response.data);
      setError(null);
    } catch (err) {
      console.error("Fehler beim Abrufen der Mieter", err);
      setError("Fehler beim Abrufen der Mieter");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await tenantAPI.getStats();
      setStats(response.data);
    } catch (err) {
      console.error("Fehler beim Abrufen der Statistiken", err);
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchStats();
  }, [page, rowsPerPage, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = async (tenant = null) => {
    await fetchProperties();

    if (tenant) {
      setCurrentTenant(tenant);
      setFormData({
        tenant_number: tenant.tenant_number,
        name: tenant.name,
        email: tenant.email || "",
        phone: tenant.phone || "",
        property_ids: (tenant.properties || []).map((p) => p.id),
        rent_net: tenant.rent_net || "",
        utility_advance: tenant.utility_advance || "",
        contract_size: tenant.contract_size || "",
        deposit_amount: tenant.deposit_amount || "",
        bank_account: tenant.bank_account || "",
        notice_period: tenant.notice_period || "",
        move_in_date: tenant.move_in_date || "",
        move_out_date: tenant.move_out_date || "",
        last_rent_increase: tenant.last_rent_increase || "",
        is_active: tenant.is_active !== undefined ? tenant.is_active : true,
        description: tenant.description || "",
        contact_persons: tenant.contact_persons || [],
      });
    } else {
      setCurrentTenant(null);
      setFormData(initialFormState);
    }
    setOpenDialog(true);
  };

  useEffect(() => {
    if (openDialog) {
      setSaving(false);
    }
  }, [openDialog]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentTenant(null);
    setFormData(initialFormState);
  };

  const handleDeleteClick = (tenant) => {
    setCurrentTenant(tenant);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await tenantAPI.deleteTenant(currentTenant.id);
      fetchTenants();
      fetchStats();
      setOpenDeleteDialog(false);
      setCurrentTenant(null);
    } catch (err) {
      console.error("Fehler beim Löschen des Mieters", err);
      setError("Fehler beim Löschen des Mieters");
    }
  };

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddContactPerson = () => {
    setFormData((prev) => ({
      ...prev,
      contact_persons: [
        ...prev.contact_persons,
        { name: "", phone: "", email: "", role: "" },
      ],
    }));
  };

  const handleRemoveContactPerson = (index) => {
    setFormData((prev) => ({
      ...prev,
      contact_persons: prev.contact_persons.filter((_, i) => i !== index),
    }));
  };

  const handleContactPersonChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.contact_persons];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, contact_persons: updated };
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Prepare data, converting empty strings to null where necessary or letting backend handle it
      const submissionData = { ...formData };
      if (!submissionData.move_in_date) submissionData.move_in_date = null;
      if (!submissionData.move_out_date) submissionData.move_out_date = null;
      if (!submissionData.last_rent_increase)
        submissionData.last_rent_increase = null;
      if (submissionData.rent_net === "") submissionData.rent_net = null;
      if (submissionData.utility_advance === "")
        submissionData.utility_advance = null;
      if (!submissionData.description) submissionData.description = null;
      submissionData.contact_persons = (
        submissionData.contact_persons || []
      ).filter((cp) => cp.name?.trim());

      // Ensure property_ids is sent as array of numbers
      submissionData.property_ids = (submissionData.property_ids || []).map(
        (id) => Number(id),
      );

      if (currentTenant) {
        await tenantAPI.updateTenant(currentTenant.id, submissionData);
      } else {
        await tenantAPI.createTenant(submissionData);
      }
      fetchTenants();
      fetchStats();
      handleCloseDialog();
    } catch (err) {
      console.error("Fehler beim Speichern des Mieters", err);
      setError(
        "Fehler beim Speichern des Mieters. Stellen Sie sicher, dass die Mieternummer eindeutig ist.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await tenantAPI.importTenantsFromXlsx(file);
      fetchStats();
      fetchTenants();
    } catch (err) {
      console.error("Fehler beim Importieren der Mieter", err);
      setError("Fehler beim Importieren der Mieter");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Mieter
      </Typography>

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={2}>
              <CardContent sx={{ pb: 2 }}>
                <Typography
                  color="textSecondary"
                  variant="subtitle2"
                  gutterBottom
                >
                  Mieter Gesamt
                </Typography>
                <Typography variant="h5">{stats.total_tenants}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={2}>
              <CardContent sx={{ pb: 2 }}>
                <Typography
                  color="textSecondary"
                  variant="subtitle2"
                  gutterBottom
                >
                  Aktive Mieter
                </Typography>
                <Typography variant="h5">{stats.active_tenants}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={2}>
              <CardContent sx={{ pb: 2 }}>
                <Typography
                  color="textSecondary"
                  variant="subtitle2"
                  gutterBottom
                >
                  Ø Nettomiete
                </Typography>
                <Typography variant="h5">
                  {stats.average_rent_net?.toLocaleString("de-DE", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  €
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={2}>
              <CardContent sx={{ pb: 2 }}>
                <Typography
                  color="textSecondary"
                  variant="subtitle2"
                  gutterBottom
                >
                  Ø Vertragsgröße
                </Typography>
                <Typography variant="h5">
                  {stats.average_contract_size?.toLocaleString("de-DE", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  m²
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={2}>
              <CardContent sx={{ pb: 2 }}>
                <Typography
                  color="textSecondary"
                  variant="subtitle2"
                  gutterBottom
                >
                  Ø Preis/m²
                </Typography>
                <Typography variant="h5">
                  {stats.average_price_per_sqm?.toLocaleString("de-DE", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  €
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <TextField
          label="Suche"
          variant="outlined"
          size="small"
          value={search}
          onChange={handleSearchChange}
          placeholder="Suche nach Name, Nummer..."
        />
        <Box>
          <Button
            variant="outlined"
            onClick={fetchTenants}
            startIcon={<Refresh />}
            sx={{ mr: 1 }}
          >
            Aktualisieren
          </Button>
          <IconButton
            onClick={() => setOpenImportInfoDialog(true)}
            sx={{ mr: 1 }}
            color="primary"
          >
            <Info />
          </IconButton>
          <input
            accept=".xlsx, .xls"
            style={{ display: "none" }}
            id="raised-button-file-tenant"
            type="file"
            onChange={handleImport}
          />
          <label htmlFor="raised-button-file-tenant">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFile />}
              sx={{ mr: 1 }}
            >
              Import
            </Button>
          </label>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Mieter hinzufügen
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nummer</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Immobilie</TableCell>
              <TableCell>Preis/m²</TableCell>
              <TableCell>Startdatum</TableCell>
              <TableCell>Aktiv</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.map((tenant) => (
              <React.Fragment key={tenant.id}>
                <TableRow
                  hover
                  onClick={() =>
                    setExpandedTenantId(
                      expandedTenantId === tenant.id ? null : tenant.id,
                    )
                  }
                  sx={{
                    cursor: "pointer",
                    backgroundColor: tenant.is_active
                      ? "inherit"
                      : "rgba(158, 158, 158, 0.12)",
                    opacity: tenant.is_active ? 1 : 0.75,
                  }}
                >
                  <TableCell>{tenant.tenant_number}</TableCell>
                  <TableCell>{tenant.name}</TableCell>
                  <TableCell>
                    {tenant.properties && tenant.properties.length > 0
                      ? tenant.properties.map((prop, i) => (
                          <React.Fragment key={prop.id}>
                            {i > 0 && ", "}
                            <a
                              href={`/asset-overview?tab=properties&search=${prop.id}`}
                              style={{
                                color: "#1976d2",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                              rel="noopener noreferrer"
                            >
                              ({prop.property_number}) {prop.address}
                              {prop.floor ? `, ${prop.floor}` : ""}
                              {prop.position
                                ? ` ${
                                    positionLabels[prop.position] ||
                                    prop.position
                                  }`
                                : ""}
                            </a>
                          </React.Fragment>
                        ))
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {tenant.rent_net && tenant.contract_size
                      ? (
                          parseFloat(tenant.rent_net) /
                          parseFloat(tenant.contract_size)
                        ).toFixed(2) + " €"
                      : "-"}
                  </TableCell>
                  <TableCell>{tenant.move_in_date}</TableCell>
                  <TableCell>{tenant.is_active ? "Ja" : "Nein"}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(tenant);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(tenant);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={7}
                  >
                    <Collapse
                      in={expandedTenantId === tenant.id}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ margin: 2 }}>
                        <Typography variant="h6" gutterBottom component="div">
                          Details
                        </Typography>
                        <Grid container spacing={2}>
                          {/* Property Details */}
                          <Grid item xs={12}>
                            <Typography variant="body2">
                              <strong>Immobilien:</strong>{" "}
                              {tenant.properties && tenant.properties.length > 0
                                ? tenant.properties.map((prop, i) => (
                                    <React.Fragment key={prop.id}>
                                      {i > 0 && ", "}
                                      <a
                                        href={`/asset-overview?tab=properties&search=${prop.id}`}
                                        style={{
                                          color: "#1976d2",
                                          textDecoration: "underline",
                                          cursor: "pointer",
                                        }}
                                        rel="noopener noreferrer"
                                      >
                                        ({prop.property_number}) {prop.address}
                                        {prop.floor ? `, ${prop.floor}` : ""}
                                        {prop.position
                                          ? ` ${
                                              positionLabels[prop.position] ||
                                              prop.position
                                            }`
                                          : ""}
                                      </a>
                                    </React.Fragment>
                                  ))
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>E-Mail:</strong> {tenant.email || "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Nettomiete:</strong>{" "}
                              {tenant.rent_net ? tenant.rent_net + " €" : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Telefon:</strong> {tenant.phone || "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" sx={{ minWidth: 200 }}>
                              <strong>Nebenkosten-Vorauszahlung:</strong>{" "}
                              {tenant.utility_advance || "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Vertragsgröße:</strong>{" "}
                              {tenant.contract_size
                                ? `${tenant.contract_size} m²`
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Kaution:</strong>{" "}
                              {tenant.deposit_amount
                                ? tenant.deposit_amount + " €"
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Auszugsdatum:</strong>{" "}
                              {tenant.move_out_date || "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Letzte Mieterhöhung:</strong>{" "}
                              {tenant.last_rent_increase || "-"}
                            </Typography>
                          </Grid>
                          {tenant.description && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Beschreibung:</strong>{" "}
                                {tenant.description}
                              </Typography>
                            </Grid>
                          )}
                          {tenant.contact_persons &&
                            tenant.contact_persons.length > 0 && (
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Ansprechpartner:</strong>
                                </Typography>
                                <ul style={{ paddingLeft: 18, margin: 0 }}>
                                  {tenant.contact_persons.map((cp, i) => (
                                    <li key={i}>
                                      <Typography variant="body2">
                                        {cp.name}
                                        {cp.role ? ` (${cp.role})` : ""}
                                        {cp.phone ? ` – Tel: ${cp.phone}` : ""}
                                        {cp.email
                                          ? ` – E-Mail: ${cp.email}`
                                          : ""}
                                      </Typography>
                                    </li>
                                  ))}
                                </ul>
                              </Grid>
                            )}
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {tenants.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Keine Mieter gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={-1}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Zeilen pro Seite:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`
        }
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Person sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {currentTenant ? "Mieter bearbeiten" : "Neuen Mieter erstellen"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {currentTenant
                  ? "Aktualisieren Sie die Mieterinformationen"
                  : "Füllen Sie die erforderlichen Felder aus, um einen neuen Mieter hinzuzufügen"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={4}>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e3f2fd",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <Person color="primary" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Persönliche Informationen
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Mieternummer"
                          name="tenant_number"
                          value={formData.tenant_number}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.is_active}
                              onChange={handleFormChange}
                              name="is_active"
                              sx={{
                                color: "primary.main",
                                "&.Mui-checked": {
                                  color: "primary.main",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ fontWeight: 500 }}>
                              Aktiver Mieter
                            </Typography>
                          }
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Contact */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e8f5e8",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <ContactMail color="success" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "success.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Kontaktinformationen
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="E-Mail"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Telefon"
                          name="phone"
                          value={formData.phone}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Contract Data */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #fff3e0",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <Description color="warning" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "warning.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Vertragsdaten
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          multiple
                          options={properties}
                          getOptionLabel={(option) =>
                            option && option.address && option.property_number
                              ? `${option.address} (${option.property_number})`
                              : ""
                          }
                          value={properties.filter((p) =>
                            (formData.property_ids || []).includes(p.id),
                          )}
                          onChange={(event, newValues) => {
                            setFormData((prev) => ({
                              ...prev,
                              property_ids: newValues.map((v) => v.id),
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              label="Immobilien auswählen"
                              placeholder="Adresse oder Nummer suchen..."
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  minWidth: 250,
                                },
                              }}
                            />
                          )}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Vertragsgröße (m²)"
                          name="contract_size"
                          type="number"
                          value={formData.contract_size}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Nettomiete (€)"
                          name="rent_net"
                          type="number"
                          value={formData.rent_net}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Preis/m² (€)"
                          value={
                            formData.rent_net && formData.contract_size
                              ? (
                                  parseFloat(formData.rent_net) /
                                  parseFloat(formData.contract_size)
                                ).toFixed(2)
                              : "-"
                          }
                          slotProps={{
                            input: {
                              readOnly: true,
                            },
                          }}
                          variant="filled"
                          sx={{
                            "& .MuiFilledInput-root": {
                              borderRadius: 2,
                              backgroundColor: "grey.100",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Nebenkostenvorauszahlung (€)"
                          name="utility_advance"
                          type="number"
                          value={formData.utility_advance}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Deposit & Bank */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #fce4ec",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <AccountBalance color="error" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "error.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Kaution & Bankverbindung
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Kaution (€)"
                          name="deposit_amount"
                          type="number"
                          value={formData.deposit_amount}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Bankverbindung"
                          name="bank_account"
                          value={formData.bank_account}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Dates */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #f3e5f5",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <DateRange color="secondary" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "secondary.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Zeiträume
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Einzugsdatum"
                          name="move_in_date"
                          type="date"
                          slotProps={{ inputLabel: { shrink: true } }}
                          value={formData.move_in_date}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Auszugsdatum"
                          name="move_out_date"
                          type="date"
                          slotProps={{ inputLabel: { shrink: true } }}
                          value={formData.move_out_date}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Letzte Mieterhöhung"
                          name="last_rent_increase"
                          type="date"
                          slotProps={{ inputLabel: { shrink: true } }}
                          value={formData.last_rent_increase}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Beschreibung */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    minWidth: "550px",
                    borderRadius: 2,
                    border: "1px solid #e8f5e8",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <Description color="success" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "success.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Beschreibung
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      label="Beschreibung"
                      name="description"
                      value={formData.description ?? ""}
                      onChange={handleFormChange}
                      placeholder="Zusätzliche Informationen zum Mieter..."
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Ansprechpartner */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    minWidth: "850px",
                    borderRadius: 2,
                    border: "1px solid #e3f2fd",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <ContactMail color="primary" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Ansprechpartner
                      </Typography>
                    </Box>
                    {formData.contact_persons.map((cp, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          gap: 2,
                          mb: 2,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <TextField
                          label="Name"
                          value={cp.name ?? ""}
                          onChange={(e) =>
                            handleContactPersonChange(
                              index,
                              "name",
                              e.target.value,
                            )
                          }
                          size="small"
                          sx={{ flex: "1 1 160px" }}
                          variant="outlined"
                        />
                        <TextField
                          label="Telefon"
                          value={cp.phone ?? ""}
                          onChange={(e) =>
                            handleContactPersonChange(
                              index,
                              "phone",
                              e.target.value,
                            )
                          }
                          size="small"
                          sx={{ flex: "1 1 140px" }}
                          variant="outlined"
                        />
                        <TextField
                          label="E-Mail"
                          value={cp.email ?? ""}
                          onChange={(e) =>
                            handleContactPersonChange(
                              index,
                              "email",
                              e.target.value,
                            )
                          }
                          size="small"
                          sx={{ flex: "2 1 200px" }}
                          variant="outlined"
                        />
                        <TextField
                          label="Rolle"
                          value={cp.role ?? ""}
                          onChange={(e) =>
                            handleContactPersonChange(
                              index,
                              "role",
                              e.target.value,
                            )
                          }
                          size="small"
                          sx={{ flex: "1 1 120px" }}
                          variant="outlined"
                        />
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveContactPerson(index)}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<Add />}
                      onClick={handleAddContactPerson}
                      variant="outlined"
                      size="small"
                    >
                      Ansprechpartner hinzufügen
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
          }}
        >
          <Button
            onClick={handleCloseDialog}
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="large"
            disabled={saving}
            sx={{
              borderRadius: 2,
              px: 4,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
              },
            }}
            startIcon={
              saving ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {saving ? "Speichere..." : "Speichern"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Löschen bestätigen</DialogTitle>
        <DialogContent>
          Sind Sie sicher, dass Sie den Mieter {currentTenant?.name} löschen
          möchten?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Abbrechen</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openImportInfoDialog}
        onClose={() => setOpenImportInfoDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Format Informationen</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Für den Import von Mietern verwenden Sie bitte eine Excel-Datei
            (.xlsx) mit den folgenden Spalten (ab Zeile 2):
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Spalte</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Beschreibung</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Datentyp / Format</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>A (0)</TableCell>
                  <TableCell>Mieternummer</TableCell>
                  <TableCell>Text (Pflichtfeld)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>B (1)</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Text (Pflichtfeld)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>C (2)</TableCell>
                  <TableCell>Immobilien-/Einheitsnummer</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>D (3)</TableCell>
                  <TableCell>Nettokaltmiete</TableCell>
                  <TableCell>Zahl (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>E (4)</TableCell>
                  <TableCell>Größe (m²)</TableCell>
                  <TableCell>Zahl (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>F (5)</TableCell>
                  <TableCell>Preis/m²</TableCell>
                  <TableCell>Ignoriert</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>G (6)</TableCell>
                  <TableCell>NK Vorschuss</TableCell>
                  <TableCell>Zahl (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>H (7)</TableCell>
                  <TableCell>Einzugsdatum</TableCell>
                  <TableCell>Datum (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>I (8)</TableCell>
                  <TableCell>Auszugsdatum</TableCell>
                  <TableCell>Datum (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>J (9)</TableCell>
                  <TableCell>Telefon</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>K (10)</TableCell>
                  <TableCell>E-Mail</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>L (11)</TableCell>
                  <TableCell>Letzte Mieterhöhung</TableCell>
                  <TableCell>Datum (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>M (12)</TableCell>
                  <TableCell>Kaution</TableCell>
                  <TableCell>Zahl (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>N (13)</TableCell>
                  <TableCell>Bankverbindung</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>O (14)</TableCell>
                  <TableCell>Kündigungsfrist</TableCell>
                  <TableCell>Zahl (Optional)</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Hinweise:</Typography>
            <ul>
              <li>Die erste Zeile wird als Überschrift ignoriert.</li>
              <li>
                Beträge können im deutschen Format (z.B. 1.234,56 €) oder als
                reine Zahlen vorliegen.
              </li>
              <li>
                Datumsangaben sollten im Format TT.MM.JJJJ sein oder als
                Excel-Datum formatiert sein.
              </li>
              <li>
                Für "Kündigungsfrist" geben Sie die Anzahl der Monate als Zahl
                an.
              </li>
              <li>
                Für "Immobilien-/Einheitsnummer" verwenden Sie die entsprechende
                Immobiliennummer aus Ihrem System.
              </li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImportInfoDialog(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantTab;
